import {
	listSetupStatus,
	ListSetupRefusalReason,
	ListSetupSources,
	ListSetupView,
} from "@signalist/shared-types";

import { ROUTE_CONFIG } from "@/routes/config";

import { useGetListQuery } from "@/pages/Private/redux/list/list.api";
import { listSelector } from "@/pages/Private/redux/list/list.slice";
import { useAppSelector } from "@/redux/hooks";

import { List } from "../schema/list";
import { ListLeadCounts, listHasLeads } from "./listHasLeads";
import { listSetupSourcesOf } from "./listSourceState";

/**
 * The list's fields the shared setup predicate reads (PREC-4537). The decision itself — which
 * flags block, in which order, and what the activation refusal is — lives in
 * `@signalist/shared-types`, so this adapter cannot disagree with the API or the MCP tools.
 */
const listSetupViewOf = (
	list: List,
	leadCounts: ListLeadCounts | null | undefined
): ListSetupView => {
	const sources: ListSetupSources = listSetupSourcesOf(list);

	return {
		sourceType: list.sourceType,
		entityType: list.entityType,
		sources,
		firstCustomEventConfirmed: list.customEvents?.[0]?.confirmed === true,
		signalConfigId: list.signalConfig?.id ?? null,
		findPersonConfigured: Boolean(list.apollos?.[0]?.config?.enrichmentFilterConfig),
		hasLeads: listHasLeads(leadCounts),
	};
};

/** The setup step a refusal sends the user to: sources, signals, or the find-person settings. */
const setupStepUrl = (listId: number, reason?: ListSetupRefusalReason): string => {
	if (reason === "LIST_SETUP_SIGNALS_MISSING") {
		return `${ROUTE_CONFIG.LISTS}/create/signals/${listId}`;
	}

	if (reason === "LIST_SETUP_FIND_PERSON_MISSING") {
		return `${ROUTE_CONFIG.LISTS}/create/details/${listId}`;
	}

	if (reason) {
		return `${ROUTE_CONFIG.LISTS}/create/source/${listId}`;
	}

	return `${ROUTE_CONFIG.LISTS}/detail/${listId}`;
};

/**
 * @param list         the list to judge, carrying its per-source relation arrays.
 * @param leadCounts   where to read lead presence from, when that is a different payload than
 *                     `list` — the full list carries the source arrays but no counts, so its
 *                     callers hand in the lean list they already hold (PREC-4118).
 */
export const getListConfigurationStatus = (
	list: List,
	leadCounts: ListLeadCounts | null | undefined = list
) => {
	const {
		activation,
		sourceState: _sourceState,
		...flags
	} = listSetupStatus(listSetupViewOf(list, leadCounts));

	return {
		...flags,
		url: setupStepUrl(list.id, activation.allowed ? undefined : activation.reason),
	};
};

const getDefaultStatus = (listId: number) => ({
	findPersonAtCompanyMissing: false,
	sourceMissing: false,
	sourceManualOnly: false,
	scrapingNotConfirmed: false,
	scrapingNotRequested: false,
	scrapingOngoing: false,
	signalsMissing: false,
	url: `${ROUTE_CONFIG.LISTS}/detail/${listId}`,
});

/**
 * @param leadCounts the lean list the caller already holds, read only for its lead counts — the
 * full list this hook fetches carries the source arrays but no counts at all, so without it a
 * hand-populated list cannot be told apart from an unconfigured one (PREC-4118).
 */
export const useListConfigurationStatus = (listId: number, leadCounts?: ListLeadCounts | null) => {
	const { isLoading, isFetching } = useGetListQuery(listId, { skip: !listId });
	const { currentFullList } = useAppSelector(listSelector);

	// Read the *full* list (with source arrays), not `currentList` — the latter is routinely
	// clobbered by the lean `getLeanList` poll / overview / `updateList` matchers, which drop the
	// source arrays and would make `sourceMissing` wrongly true (e.g. Re-Run disabled even though
	// the list has a source). See `currentFullList` in the list slice. (PREC-3621)
	const list = currentFullList && currentFullList.id === listId ? currentFullList : null;

	if (!list) {
		return { ...getDefaultStatus(listId), isLoading: isLoading || isFetching };
	}

	return {
		...getListConfigurationStatus(list, leadCounts ?? list),
		isLoading: isLoading || isFetching,
	};
};
